import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { MessageSquareText } from 'lucide-react';
import { useState } from 'react';
import { AiFeedbackDetailsDrawer } from './AiFeedbackDetailsDrawer';
import { AiFeedbackFilters } from './AiFeedbackFilters';
import { useAiFeedbackColumns } from './columns';

export const Route = createFileRoute('/admin-console/ai/ai-feedback')({
    staticData: {
        permissions: ['admin_ai:read'],
        breadcrumb: {
            title: 'AI feedback',
        },
        ui: {
            icon: MessageSquareText,
            title: 'AI feedback',
            subtitle: 'Review user feedback on AI breach analysis across all organisations',
            widthVariant: 'fullWidth',
            menuDescription: 'Review AI breach analysis feedback',
        },
    },
    component: AiFeedbackPage,
});

type AiFeedbackItem = RouterOutputs['admin']['aiFeedback']['list']['rows'][number];
type QueryVariables = RouterInputs['admin']['aiFeedback']['list'];

function AiFeedbackPage() {
    const { trpc } = useTrpc();

    const [selectedItem, setSelectedItem] = useState<AiFeedbackItem | undefined>();
    const [filters, setFilters] = useState<Omit<QueryVariables, 'limit' | 'offset'>>({});

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
    });

    const { data, refetch, isFetching, error } = trpc.admin.aiFeedback.list.useQuery({
        ...queryVariables,
        ...filters,
    });

    const columns = useAiFeedbackColumns(setSelectedItem);
    const hasBlockingError = hasBlockingQueryError(error, data);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-ai-feedback-table-columns',
            enableReordering: true,
            defaultHidden: ['correlationId'],
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TableToolbar filters={<AiFeedbackFilters onChange={setFilters} />} variant="enhanced" />

            <AiFeedbackDetailsDrawer
                item={selectedItem}
                open={!!selectedItem && !hasBlockingError}
                onClose={() => setSelectedItem(undefined)}
            />

            {hasBlockingError ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't load AI feedback"
                />
            ) : (
                <>
                    {error && (
                        <InlineQueryError
                            error={error}
                            onRetry={() => void refetch()}
                            retrying={isFetching}
                            title="Couldn't refresh AI feedback"
                            compact
                        />
                    )}
                    <PrismaTable {...tableProps} />
                </>
            )}
        </PageController>
    );
}
